// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * How use it:
 * 1. by dispatch:
 *      dispatch({ type: "xCommon/update", data: { key: "isTabletScreen", value: true } });
 *      data => it has "key" -> object key and value
 * 2. by handler:
 *      xCommonUpdate({ key: "isTabletScreen", value: true });
 */
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { xCommonHandlerType } from "@keller/App/Handlers/XNG/Common";
import { isMobileScreen, isSmallMobileScreen, isTabletScreen, isWideScreen } from "@keller/XDL/Utilities";
import { Draft, original } from "immer";

export type xCommonStateType = Readonly<{
    loadingOverlay?: string[];
    isSmallMobileScreen?: boolean;
    isMobileScreen?: boolean;
    isTabletScreen?: boolean;
    isWideScreen?: boolean;
    lastCategoryPathRequested?: string;
    [key: string]: any;
}>;

export const initialXCommon: xCommonStateType = {
    loadingOverlay: [],
    isSmallMobileScreen: isSmallMobileScreen(),
    isMobileScreen: isMobileScreen(),
    isTabletScreen: isTabletScreen(),
    isWideScreen: isWideScreen(),
    lastCategoryPathRequested: undefined,
};

const reducer = {
    "xCommon/update": (draft: Draft<xCommonStateType>, action: { data: xCommonHandlerType }) => {
        if (action.data.key) {
            draft[action.data.key] = action.data.value;
        }
    },
    "xCommon/loadingOverlay": (draft: Draft<xCommonStateType>, action: { data: xCommonHandlerType }) => {
        if (action.data.key) {
            if (action.data.key === "add") {
                draft.loadingOverlay?.push(action.data.value);
            } else if (action.data.key === "remove") {
                // action.data.value is string/array
                const currentStore = original(draft) as xCommonStateType;
                draft.loadingOverlay = currentStore.loadingOverlay?.filter(
                    (item: string) => action.data.value.indexOf(item) === -1,
                );
            } else if (action.data.key === "clear") {
                draft.loadingOverlay = [];
            }
        }
    },
};

const xCommon = createTypedReducerWithImmer(initialXCommon, reducer);

(reducers as any).xCommon = xCommon;

export type xCommonState = {
    xCommon: xCommonStateType;
} & ApplicationState;

type xCommonReducers = {
    [key: string]: typeof xCommon;
};

export type xCommonActions = Parameters<xCommonReducers[keyof xCommonReducers]>[1] | AnyAction;
